import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';

export interface AgentDefinition {
    key: string;
    nombre: string;
    descripcion: string;
    icono: string;
    color: string;
}

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    logId?: number | null;
    rating?: 1 | -1 | null;
    isError?: boolean;
    errorDetail?: string;
}

const STORAGE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_MESSAGES   = 30;

function storageKey(agentKey: string): string {
    return `dribling_agent_${agentKey}`;
}

function saveMessages(agentKey: string, msgs: ChatMessage[]): void {
    const trimmed = msgs.slice(-MAX_MESSAGES);
    localStorage.setItem(storageKey(agentKey), JSON.stringify({
        messages:  trimmed,
        timestamp: Date.now(),
    }));
}

function loadMessages(agentKey: string): ChatMessage[] {
    try {
        const raw = localStorage.getItem(storageKey(agentKey));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.timestamp > STORAGE_TTL_MS) {
            localStorage.removeItem(storageKey(agentKey));
            return [];
        }
        return parsed.messages ?? [];
    } catch {
        return [];
    }
}

export const useAgentStore = defineStore('agent', () => {
    const agentes          = ref<AgentDefinition[]>([]);
    const selectedAgent    = ref<AgentDefinition | null>(null);
    const messages         = ref<ChatMessage[]>([]);
    const loading          = ref(false);
    const configLoaded     = ref(false);
    const userName         = ref('');
    const organizacionNombre = ref('');

    async function loadConfig() {
        if (configLoaded.value) return;
        try {
            const { data } = await axios.get('/agent/config');
            agentes.value          = data.agentes;
            userName.value         = data.usuario;
            organizacionNombre.value = data.organizacion ?? '';
            configLoaded.value     = true;
        } catch (e) {
            // user not authenticated or no agents configured
        }
    }

    function selectAgent(agent: AgentDefinition) {
        if (selectedAgent.value?.key !== agent.key) {
            messages.value = loadMessages(agent.key);
        }
        selectedAgent.value = agent;
    }

    function clearAgent() {
        selectedAgent.value = null;
        messages.value      = [];
    }

    async function sendMessage(text: string) {
        if (!selectedAgent.value || loading.value) return;

        messages.value.push({ role: 'user', content: text });
        loading.value = true;

        // Keep only last 10 messages for context window
        const history = messages.value.slice(-10, -1);

        try {
            const { data } = await axios.post('/agent/chat', {
                agent:   selectedAgent.value.key,
                message: text,
                history,
            });
            messages.value.push({ role: 'assistant', content: data.response, logId: data.log_id ?? null, rating: null });
        } catch (e: any) {
            const status      = e.response?.status;
            const serverError = e.response?.data?.error ?? e.message ?? 'Error desconocido';

            // Friendly message based on HTTP status
            let friendlyMsg = 'Ocurrió un problema al procesar tu consulta. Por favor intenta de nuevo.';
            if (status === 402) {
                friendlyMsg = 'No hay neuronas disponibles para este período. Contacta al administrador para recargar.';
            } else if (status === 403) {
                friendlyMsg = 'No tienes acceso a este agente.';
            } else if (status === 422) {
                friendlyMsg = 'La consulta no pudo ser procesada. Intenta reformular tu pregunta.';
            }

            messages.value.push({
                role: 'assistant',
                content: friendlyMsg,
                logId: null,
                rating: null,
                isError: true,
                errorDetail: `[${status ?? 'red'}] ${serverError}`,
            });
        } finally {
            loading.value = false;
            if (selectedAgent.value) {
                saveMessages(selectedAgent.value.key, messages.value);
            }
        }
    }

    function clearHistory() {
        if (selectedAgent.value) {
            localStorage.removeItem(storageKey(selectedAgent.value.key));
        }
        messages.value = [];
    }

    async function rateMessage(logId: number, rating: 1 | -1, pregunta: string, respuesta: string) {
        // Find and update locally first (optimistic)
        const msg = messages.value.find(m => m.logId === logId);
        if (msg) msg.rating = rating;

        try {
            await axios.post('/agent/rating', { log_id: logId, rating, pregunta, respuesta });
            if (selectedAgent.value) {
                saveMessages(selectedAgent.value.key, messages.value);
            }
        } catch {
            // silently ignore — rating is non-critical
        }
    }

    return {
        agentes, selectedAgent, messages, loading, configLoaded,
        userName, organizacionNombre,
        loadConfig, selectAgent, clearAgent, sendMessage, clearHistory, rateMessage,
    };
});
